import { useEffect, useMemo, useState } from "react";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import Timeoff from "../models/Timeoff";

import "chartjs-adapter-date-fns";
import { enUS } from "date-fns/locale";

import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartDataset,
  ChartOptions,
  Legend,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import { addDays, addMonths, format, subDays, subMonths } from "date-fns";
import { Bar } from "react-chartjs-2";
import { TimeOffStatus, TimeOffType } from "../types/TimeOffType";
import { arrayFromZeroToN } from "../utils";
import { ButtonComponent } from "./ButtonComponent";
import { PresentToAll, Today } from "@material-ui/icons";

ChartJS.register(CategoryScale, TimeScale, LinearScale, BarElement, Title, Tooltip, Legend);

const getColorsByStatus = (uniqueIds: string[], timeoffList: Timeoff[], datasetIndex: number, options: { opacity: number }): string[] => {
  return uniqueIds.map((email) => {
    const status = timeoffList?.filter(({ requester }) => requester.email === email)[datasetIndex]?.status;
    return status === TimeOffStatus.PENDING
      ? `rgba(155, 155, 155, ${options.opacity})`
      : status === TimeOffStatus.PENDING_CANCELLATION
      ? `rgba(255, 234, 49, ${options.opacity})`
      : status === TimeOffStatus.ADD_DOCUMENTATION
      ? `rgba(68, 189, 50, ${options.opacity})`
      : status === TimeOffStatus.APPROVED
      ? `rgba(42, 250, 10, ${options.opacity})`
      : status === TimeOffStatus.REJECTED
      ? `rgba(255, 51, 51, ${options.opacity})`
      : `#000`;
  });
};

const calculateEndDate = (timeoff: Timeoff): Date => {
  return addDays(timeoff.shortEndDate, 1);
};

export const options: ChartOptions<"bar"> = {
  indexAxis: "y" as const,
  scales: {
    x: {
      offset: false,
      type: "time",
      time: {
        unit: "day",
        stepSize: 1,
      },
      ticks: {
        align: "start",
      },
      grid: {
        display: false,
      },
      adapters: {
        date: {
          locale: enUS,
        },
      },
    },
    y: {
      beginAtZero: true,
      stacked: true,
    },
  },
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: true,
      text: "Timeoffs",
      fullSize: true,
    },
  },
};

export const data: ChartData<"bar", string[][]> = {
  datasets: [],
};

interface GanttChartProps {
  fetchData: () => Promise<PagedResult<Timeoff>>;
  filters: any[];
}

interface OptionsProps {
  goBack: () => void;
  next: () => void;
  present: Date;
  currentChartDate: Date;
}

type TChartData = { data: typeof data; options: typeof options };

const GanttChart = ({ fetchData, filters }: GanttChartProps) => {
  const [timeoffList, setTimeoffList] = useState<Timeoff[]>();
  const [today, setToday] = useState<Date>(new Date());

  useEffect(() => {
    setTimeoffList([]);
    fetchData().then((timeoffs) => setTimeoffList(timeoffs.content));
  }, [...filters, fetchData]);

  const previousMonth = () => setToday((prev) => subMonths(prev, 1));
  const nextMonth = () => setToday((prev) => addMonths(prev, 1));

  const uniqueIds = useMemo(() => [...new Set(timeoffList?.map(({ requester }) => requester.email))], [timeoffList]);
  console.log(timeoffList);
  console.log(uniqueIds);
  const datasetsQuantity: number = useMemo(() => {
    const map = new Map<string, number>();
    timeoffList?.forEach(({ requester }) => {
      map.set(requester.email, (map.get(requester.email) || 0) + 1);
    });
    return Math.max(...map.values());
  }, [timeoffList]);

  //@ts-ignore
  const memoData: TChartData = useMemo(() => {
    return {
      data: {
        ...data,
        labels: uniqueIds
          .map((email) => timeoffList?.find(({ requester }) => requester.email === email)?.requester)
          .map((requester) => `${requester?.firstName} ${requester?.lastName}`),
        datasets: arrayFromZeroToN(datasetsQuantity).map<ChartDataset<"bar", (false | string[])[]>>((datasetIndex) => {
          return {
            borderSkipped: false,
            borderColor: getColorsByStatus(uniqueIds, timeoffList!, datasetIndex, { opacity: 1 }),
            backgroundColor: getColorsByStatus(uniqueIds, timeoffList!, datasetIndex, { opacity: 0.9 }),
            data: uniqueIds.map((email) => {
              const timeoff = timeoffList?.filter(({ requester }) => email === requester.email)[datasetIndex] || false;
              if (!timeoff) return false;

              const endDate = calculateEndDate(timeoff);
              return [format(timeoff.shortStartDate, "yyyy-MM-dd"), format(endDate, "yyyy-MM-dd")];
            }),
          };
        }),
      },
      options: {
        ...options,
        datasets: {
          ...options.datasets,
        },
        scales: {
          ...options.scales,
          x: {
            ...options.scales!.x,
            min: subDays(today, 7),
            max: addDays(today, 23),
          },
        },
        plugins: {
          ...options.plugins,
          tooltip: {
            backgroundColor: "#fff",
            bodyColor: "#000",
            bodySpacing: 2,
            position: "average",
            xAlign: "left",
            yAlign: "center",
            displayColors: false,
            callbacks: {
              beforeBody: (ctx) => {
                const userTimeoffs = timeoffList?.filter(({ requester }) => requester.email === uniqueIds[ctx[0].dataIndex]);
                const date: [string, string] = ctx[0].dataset.data[ctx[0].dataIndex] as unknown as [string, string];

                const timeoff = userTimeoffs?.find((t) => {
                  const endDate = calculateEndDate(t);
                  return date[0] === format(t.shortStartDate, "yyyy-MM-dd") && date[1] === format(endDate, "yyyy-MM-dd");
                });

                const info: string[] = [];
                const endDate = calculateEndDate(timeoff!);
                info.push(
                  `${timeoff!.requester.firstName} ${timeoff!.requester.lastName}: ${format(
                    timeoff!.shortStartDate,
                    "MMM-dd-yyyy"
                  )} - ${format(timeoff!.shortEndDate, "MMM-dd-yyyy")}`
                );
                info.push(`Total of days: ${timeoff!.totalDays}`);
                info.push(`Reason: ${timeoff!.type}`);
                info.push(`Status: ${timeoff!.status}`);
                return info;
              },
            },
            external: ({ chart, tooltip }) => {
              tooltip.body[0].lines.shift();
            },
          },
        },
      },
    };
  }, [timeoffList, today]);

  return (
    <>
      {uniqueIds?.length ? (
        <>
          <Bar height={uniqueIds.length * 8 + 20} options={memoData.options} data={memoData.data} />
          <Options goBack={previousMonth} next={nextMonth} present={new Date()} currentChartDate={today} />{" "}
        </>
      ) : (
        <h4>No data to display</h4>
      )}
    </>
  );
};

const Options = ({ goBack, next, present, currentChartDate }: OptionsProps) => {
  return (
    <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <ButtonComponent
        disabled={present.getMonth() == currentChartDate.getMonth()}
        style={{ backgroundColor: "#F59E33" }}
        size="medium"
        onClick={goBack}
        text={`Previous Month`}
      />

      <ButtonComponent style={{ backgroundColor: "#F59E33" }} size="medium" onClick={next} text={`Next Month`} />
    </div>
  );
};

export default GanttChart;
